import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { TableRow, TableCell, Checkbox } from '@mui/material'

import GpsVersionModal from 'components/form/course/GpsVersionModal'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import SimpleTable from 'components/ui/SimpleTable'
import { selectCourse } from 'store/course/slice'
import { selectLocale } from 'store/locale/slice'
import { localizedDateTime } from 'utils/formatDate'
import {
  setCourseGpsVersionOfficial,
  setCourseGpsVersionReviewed,
  setCourseGpsVersionUnofficial,
} from 'store/course/thunks'

interface Props {
  course: Course
}

export default function CourseGpsVersions({ course }: Props): JSX.Element {
  const [visibleCount, setVisibleCount] = useState<number>(5)
  const [versionDialogOpen, setVersionDialogOpen] = useState<boolean>(false)
  const [selectedGpsVersion, setSelectedGpsVersion] = useState<number | null>(null)
  const [dispatchingGpsVersion, setDispatchingGpsVersion] = useState<number | null>(null)
  const intl = useIntl()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const { gpsVersionOfficialStatus } = useSelector(selectCourse)

  const getSortedVersions = useCallback(() => {
    const gpsPoints = course.gpsPoints || []
    const versions = [...new Map(gpsPoints.map((gps: CourseGps) => [gps.gpsVersion, gps])).values()]
    return versions.sort((a, b) => {
      const bVersion = b.gpsVersion || 0
      const aVersion = a.gpsVersion || 0
      return bVersion > aVersion ? 1 : -1
    })
  }, [course])

  const getPlayableHoleCount = useCallback(
    (gpsVersion: number | null) => {
      if (gpsVersion === null) {
        return 0
      }

      const versionPoints = (course.gpsPoints || []).filter((gps: CourseGps) => gps.gpsVersion === gpsVersion)
      const teeboxHoles = new Set<number>()
      const greenHoles = new Set<number>()

      versionPoints.forEach((gps: CourseGps) => {
        if (gps.locationType === 'teebox') {
          teeboxHoles.add(gps.hole)
        } else if (gps.locationType === 'green') {
          greenHoles.add(gps.hole)
        }
      })

      let count = 0
      teeboxHoles.forEach((hole) => {
        if (greenHoles.has(hole)) {
          count += 1
        }
      })

      return count
    },
    [course],
  )

  useEffect(() => {
    const versions = getSortedVersions()
    const officialVersionIndex = versions.findIndex((gps: CourseGps) => gps.isOfficial)
    const newVisibleCount = officialVersionIndex && officialVersionIndex > 4 ? officialVersionIndex + 1 : 0
    if (officialVersionIndex && newVisibleCount > 0) {
      setVisibleCount(newVisibleCount)
    }
  }, [getSortedVersions])

  const getGpsVersions = () => {
    return getSortedVersions().slice().splice(0, visibleCount)
  }

  const columns: TableColumn[] = [
    {
      id: 'version',
      label: intl.formatMessage({ id: 'strings.version' }),
    },
    {
      id: 'creatorName',
      label: intl.formatMessage({ id: 'strings.creatorName' }),
    },
    {
      id: 'creatorEmail',
      label: intl.formatMessage({ id: 'strings.creatorEmail' }),
    },
    {
      id: 'status',
      label: intl.formatMessage({ id: 'strings.status' }),
    },
    {
      id: 'created',
      label: intl.formatMessage({ id: 'strings.created' }),
    },
    {
      id: 'view',
      label: '',
    },
    { id: 'reviewed', label: intl.formatMessage({ id: 'strings.reviewed' }) },
    {
      id: 'official',
      label: '',
    },
  ]

  const getVersionNumber = (gps: CourseGps) => {
    return gps.gpsVersion || '-'
  }

  const openGpsVersion = (gps: CourseGps) => {
    setVersionDialogOpen(true)
    setSelectedGpsVersion(gps.gpsVersion)
  }

  const getVersionStatus = (gps: CourseGps) => {
    return gps.isOfficial
      ? intl.formatMessage({ id: 'strings.official' })
      : intl.formatMessage({ id: 'strings.unofficial' })
  }

  const getUpdatedAtDate = (gps: CourseGps) => {
    const date = gps?.createdAt ? new Date(gps?.createdAt.date) : null
    if (!date) {
      return '-'
    }
    return localizedDateTime(date, locale.language.code)
  }

  const getOfficialLinkText = (gps: CourseGps) => {
    const make = intl.formatMessage({ id: 'strings.make' })
    const official = gps.isOfficial
      ? intl.formatMessage({ id: 'strings.unofficial' })
      : intl.formatMessage({ id: 'strings.official' })
    return `${make} ${official}`
  }

  const submitGpsOfficial = (gps: CourseGps) => {
    if (gps.isOfficial) {
      makeGpsUnofficial(gps)
      return
    }
    makeGpsOfficial(gps)
  }

  const makeGpsOfficial = (gps: CourseGps) => {
    setSelectedGpsVersion(gps.gpsVersion)
    dispatch(setCourseGpsVersionOfficial({ courseId: course.id, data: { gpsVersion: gps.gpsVersion } }))
  }

  const makeGpsUnofficial = (gps: CourseGps) => {
    setSelectedGpsVersion(gps.gpsVersion)
    dispatch(setCourseGpsVersionUnofficial({ courseId: course.id, data: { gpsVersion: gps.gpsVersion } }))
  }

  const showMoreVersions = () => {
    setVisibleCount(visibleCount + 5)
  }

  const handleCheckChange = async (gps: CourseGps, checked: boolean) => {
    const gpsVersion = gps.gpsVersion || 0

    setDispatchingGpsVersion(gpsVersion)
    dispatch(setCourseGpsVersionReviewed({ courseId: course.id, gpsVersion, data: { reviewed: checked } }))
    setDispatchingGpsVersion(null)
  }

  return (
    <>
      <GpsVersionsContainer>
        <SectionTitle>{intl.formatMessage({ id: 'strings.gpsVersions' })}</SectionTitle>
        <SimpleTable columns={columns}>
          {getGpsVersions().map((gps: CourseGps, i: number) => (
            <TableRow hover key={i}>
              <TableCell>{getVersionNumber(gps)}</TableCell>
              <TableCell>{gps.creatorName || '-'}</TableCell>
              <TableCell>{gps.creatorEmail || '-'}</TableCell>
              <TableCell>{getVersionStatus(gps)}</TableCell>
              <TableCell>{getUpdatedAtDate(gps)}</TableCell>
              <TableCell>
                <ActionText onClick={() => openGpsVersion(gps)}>
                  {intl.formatMessage({ id: 'strings.view' })}
                </ActionText>
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={gps.reviewed}
                  onChange={(e) => handleCheckChange(gps, e.target.checked)}
                  disabled={dispatchingGpsVersion === gps.gpsVersion}
                />
              </TableCell>
              <TableCell>
                {(() => {
                  const playableHoleCount = getPlayableHoleCount(gps.gpsVersion)
                  const makeOfficialDisabled = !gps.isOfficial && playableHoleCount > 1 && playableHoleCount < 18
                  return (
                    <ActionText
                      $disabled={makeOfficialDisabled}
                      onClick={() => {
                        if (makeOfficialDisabled) {
                          return
                        }
                        submitGpsOfficial(gps)
                      }}
                    >
                      {getOfficialLinkText(gps)}
                    </ActionText>
                  )
                })()}
                {gpsVersionOfficialStatus === 'loading' && gps.gpsVersion === selectedGpsVersion && (
                  <LoadingSpinnerGreen />
                )}
              </TableCell>
            </TableRow>
          ))}
        </SimpleTable>
        {getSortedVersions() && getSortedVersions().length > visibleCount && (
          <LoadMore onClick={showMoreVersions}>{intl.formatMessage({ id: 'strings.loadMore' })}</LoadMore>
        )}
      </GpsVersionsContainer>
      {versionDialogOpen && (
        <GpsVersionModal
          open={versionDialogOpen}
          close={() => setVersionDialogOpen(false)}
          course={course}
          gpsVersion={selectedGpsVersion}
        />
      )}
    </>
  )
}

const GpsVersionsContainer = styled.div`
  padding: 0 20px;

  & tbody tr {
    &:nth-child(even) {
      background-color: ${(props) => props.theme.customPalette.lightGray};
    }
  }
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  color: ${(props) => props.theme.palette.primary.main};
  cursor: pointer;
  margin-top: 20px;
  font-weight: 600;
`

const ActionText = styled.span<{ $disabled?: boolean }>`
  color: ${(props) => (props.$disabled ? props.theme.customPalette.lightGray2 : props.theme.palette.primary.main)};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
`
